import React from 'react'
import { Link } from 'react-router-dom'
import Badge from './Badge'

/**
 * One co-funder lead — a foundation that gives to the same organizations as a
 * foundation that already funds the client.
 *
 * ## The structural separation, and why it is structural
 *
 * `via_funder_name` is a **CLIENT fact**: it names one of the CLIENT'S OWN
 * funders, not this one. It is rendered in its own block, under its own
 * heading, below a divider — never inside the funder's name, location line, or
 * badges.
 *
 * That is not fussiness. On 2026-07-27 a funder blurb was generated from one
 * flat block of client + funder facts, and a live beta client's own city was
 * printed twice as though it described the foundation, because a flat block has
 * no notion of whose fact is whose. The backend already keeps the two apart
 * (`cofunder_leads` returns `via_funder_name` as its own field, and
 * `peer_evidence` has followed this rule since); the separation has to survive
 * all the way to the template or it was never real.
 *
 * Read the card aloud and the two halves never blur: "Cambridge Community
 * Foundation, Cambridge MA, accepts applications" is the foundation. "Gives to
 * 11 of the same organizations as The Boston Foundation, which funds you" is
 * the client's own history.
 *
 * ## No score
 *
 * FunderCard leads with a 0–100 match score. This card deliberately has none:
 * the affinity score is a cosine similarity discounted by seed authority, it is
 * not on the same scale as the match score, and showing two different numbers
 * both called "score" on one dashboard would invite the comparison. The shared
 * grantee count IS the evidence, and it is a fact a reader can check.
 */
export default function CoFunderCard({ lead }) {
  const location = [lead.city, lead.state].filter(Boolean).join(', ')

  return (
    <div className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all px-4 py-3">
      {/* ── The foundation's own facts ─────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {lead.profile_available ? (
            <Link
              to={`/funders/${lead.funder_id}`}
              className="font-medium text-gray-900 text-sm leading-snug hover:text-brand-500 hover:underline"
            >
              {lead.name}
            </Link>
          ) : (
            // No link rather than a link that 404s: below Pro the profile
            // endpoint serves only matched, open funders, and a co-funder lead
            // is by construction a funder the client was never matched to.
            <span className="font-medium text-gray-900 text-sm leading-snug">
              {lead.name}
            </span>
          )}
          <p className="text-xs text-gray-500 mt-0.5">
            {location || 'Location not filed'}
            {lead.filing_year && <> · Latest filing {lead.filing_year}</>}
          </p>
        </div>
        {/* `accepts_applications` is NULL for every Schedule I filer — Form 990
            has no openness box — so an unknown says "not filed" and never
            "does not accept". Silence, not a guess. */}
        <div className="flex-shrink-0">
          {lead.accepts_applications === true ? (
            <Badge variant="open">Accepts applications</Badge>
          ) : (
            <span className="text-[11px] text-gray-500">Openness not filed</span>
          )}
        </div>
      </div>

      {/* ── The client's own facts, fenced off from the block above ────── */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Why we surfaced them for you
        </p>
        <p className="text-sm text-gray-700 mt-1">
          Gives to{' '}
          <span className="font-semibold text-gray-900">
            {lead.shared_grantees} of the same organizations
          </span>{' '}
          as <span className="font-medium">{lead.via_funder_name}</span>, which
          has funded you.
        </p>
        {/* Phase D. These leads are ranked BELOW the locally-evidenced ones
            rather than removed — dropping them empties the list for 21.6% of
            Massachusetts and 39.5% of Mississippi organizations while changing
            nothing where enough local candidates exist. But demoting silently
            would leave a grant writer unable to tell the two apart, which is
            the cost ADR-0001 cares about, so the weaker evidence says so.
            `evidence_is_local === null` is "we can't tell" and stays silent. */}
        {lead.evidence_is_local === false && (
          <p className="text-xs text-amber-700 mt-1.5">
            Most of those shared organizations are outside{' '}
            {lead.state ? 'your state' : 'your area'}. This connection is real
            but may not transfer to local work. Worth a look after the ones
            above.
          </p>
        )}
      </div>
    </div>
  )
}
